import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Page, Card, Field, Input, Select, Button, Empty } from "../ui";
import { get, send } from "../lib/api";
import { t } from "../i18n";

const BU = "NUMEN";

interface FieldDef { name: string; label: string; type: "number" | "text" | "select"; default: string; options?: string[] }

// Map a calculator field's English label to its i18n key.
function fieldLabel(label: string): string {
  const map: Record<string, string> = {
    "Amount": "comp.amount", "Gross amount": "comp.grossAmount", "Province": "comp.province",
    "Transaction": "comp.transaction", "Supply": "comp.supply", "Class": "comp.class",
    "Concept": "comp.concept", "Municipality": "comp.municipality",
    "Taxable income": "comp.taxableIncome", "Tariff": "comp.tariff",
  };
  return map[label] ? t(map[label]) : label;
}
interface CalcDef { title: string; path: string; fields: FieldDef[]; money?: string; rate?: string }

function Calculator({ def }: { def: CalcDef }) {
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(def.fields.map((f) => [f.name, f.default])));
  const [res, setRes] = useState<Record<string, unknown> | null>(null);
  const run = async () => {
    const body: Record<string, unknown> = {};
    for (const f of def.fields) body[f.name] = f.type === "number" ? Number(vals[f.name]) : vals[f.name];
    setRes(await send<Record<string, unknown>>("POST", def.path, body));
  };
  return (
    <Card title={def.title}>
      {def.fields.map((f) => (
        <Field key={f.name} label={fieldLabel(f.label)}>
          {f.type === "select" ? (
            <Select value={vals[f.name]} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })}>{f.options!.map((o) => <option key={o}>{o}</option>)}</Select>
          ) : (
            <Input className={f.type === "number" ? "num" : ""} inputMode={f.type === "number" ? "decimal" : undefined} value={vals[f.name]} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })} />
          )}
        </Field>
      ))}
      <div className="form-actions"><Button onClick={run}>{t("common.calculate")}</Button></div>
      {res && (
        <table className="data statement"><tbody>
          {Object.entries(res).filter(([, v]) => typeof v !== "object").map(([k, v]) => (
            <tr key={k}><td>{k.replace(/_/g, " ")}</td><td className="num">{typeof v === "number" ? (Math.abs(v) < 1 && v !== 0 ? (v * 100).toFixed(2) + "%" : String(v)) : String(v)}</td></tr>
          ))}
        </tbody></table>
      )}
    </Card>
  );
}

const hubs: Record<string, { name: string; calcs: CalcDef[] }> = {
  CA: { name: "Canada (Alberta)", calcs: [
    { title: "GST / HST", path: "/api/v1/jurisdiction/CA/calc/gst", fields: [
      { name: "amount", label: "Amount", type: "number", default: "1000" },
      { name: "province", label: "Province", type: "select", default: "AB", options: ["AB","BC","ON","NS","QC"] },
      { name: "tx_type", label: "Transaction", type: "select", default: "SALE", options: ["SALE","PURCHASE"] },
      { name: "supply_type", label: "Supply", type: "select", default: "DOMESTIC", options: ["DOMESTIC","EXPORT"] },
    ] },
  ] },
  PR: { name: "Puerto Rico", calcs: [
    { title: "IVU (sales tax)", path: "/api/v1/jurisdiction/PR/calc/ivu", fields: [
      { name: "amount", label: "Amount", type: "number", default: "1000" },
      { name: "pr_ivu_class", label: "Class", type: "select", default: "TAXABLE_GOODS", options: ["TAXABLE_GOODS","TAXABLE_SVC_B2C","TAXABLE_SVC_B2B_DSGN","TAXABLE_SVC_B2B_OTHER"] },
    ] },
    { title: "Withholding (480.6)", path: "/api/v1/jurisdiction/PR/calc/withholding", fields: [
      { name: "gross_amount", label: "Gross amount", type: "number", default: "10000" },
      { name: "withholding_class", label: "Class", type: "select", default: "PROF_SVC_7PCT", options: ["PROF_SVC_7PCT","NON_RESIDENT_29PCT","INTEREST_10PCT","RENT_10PCT","DIVIDEND_10PCT"] },
    ] },
    { title: "Corporate income tax (1023.10)", path: "/api/v1/jurisdiction/PR/calc/corp-income-tax", fields: [
      { name: "taxable_income", label: "Taxable income", type: "number", default: "300000" },
    ] },
  ] },
  CO: { name: "Colombia", calcs: [
    { title: "IVA", path: "/api/v1/jurisdiction/CO/calc/iva", fields: [
      { name: "gross_amount", label: "Gross amount", type: "number", default: "1000000" },
      { name: "tariff_cd", label: "Tariff", type: "select", default: "GEN19", options: ["GEN19","REDUCED5","EXENTO","EXCLUIDO"] },
    ] },
    { title: "Retención en la fuente", path: "/api/v1/jurisdiction/CO/calc/retencion", fields: [
      { name: "gross_amount", label: "Gross amount", type: "number", default: "1000000" },
      { name: "concepto_cd", label: "Concept", type: "select", default: "HONORARIOS", options: ["HONORARIOS","SERVICIOS","COMPRAS","ARRENDAMIENTOS"] },
    ] },
    { title: "ICA municipal", path: "/api/v1/jurisdiction/CO/calc/ica", fields: [
      { name: "gross_amount", label: "Gross amount", type: "number", default: "3000000" },
      { name: "divipola_cd", label: "Municipality", type: "select", default: "11001", options: ["11001","05001","76001"] },
    ] },
  ] },
};

export function ComplianceHub() {
  const { code } = useParams();
  const [resolved, setResolved] = useState("");
  useEffect(() => {
    if (code) return;
    get<{ jurisdiction: string }>(`/api/v1/jurisdiction?bu=${BU}`).then((d) => setResolved(d.jurisdiction)).catch(() => setResolved("CA"));
  }, [code]);
  const active = (code || resolved || "").toUpperCase();
  const hub = hubs[active];
  return (
    <Page title={`${t("nav.compliance")}${hub ? " — " + hub.name : ""}`} actions={
      <div className="tabs">
        {Object.entries(hubs).map(([c, h]) => (
          <NavLink key={c} to={`/compliance/${c.toLowerCase()}`} className={active === c ? "tab active" : "tab"}>{h.name}</NavLink>
        ))}
      </div>
    }>
      {hub ? <div className="report-cols">{hub.calcs.map((c, i) => <Calculator key={i} def={c} />)}</div> : <Empty>…</Empty>}
    </Page>
  );
}

